import { connect, FormikProps } from 'formik'
import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button, InputV2, Modal } from '@pinchenterprisesnpm/friday-ui'
import { FooterWrapper } from '@src/ui/components/common/style'
import { selectBusinessEntity } from '@src/data/BusinessEntity/selectors'
import { extractStringFromHTML } from '@src/helpers/textHelpers'
import { IShiftIdentifierNumbersSectionFormValues } from '..'

const { Header, Content } = Modal

interface IEditNumberModalProps {
  isOpen: boolean
  closeModal: () => void
  formik?: FormikProps<IShiftIdentifierNumbersSectionFormValues>
}

export const EditNumberModal = connect<IEditNumberModalProps>(({ isOpen, closeModal, formik }) => {
  const { values, setFieldValue, errors } = formik
  const businessEntity = useSelector(selectBusinessEntity)
  const [localValue, setLocalValue] = useState(values.gig_identifier)
  const [localError, setLocalError] = useState(errors.gig_identifier || '')

  // Create validation function based on business entity policy
  const validateGigIdentifier = useMemo(() => {
    if (!businessEntity?.enabled_gig_identifier_policy || !businessEntity.gig_identifier_policy) {
      return (value: string) => {
        if (!value || value.trim() === '') {
          return 'Required'
        }
        return ''
      }
    }

    const { regex, message } = businessEntity.gig_identifier_policy
    return (value: string) => {
      if (!value || value.trim() === '') {
        return 'Required'
      }

      const cleanRegex = regex.substring(1, regex.length - 1)
      const regexPattern = new RegExp(cleanRegex)

      if (!regexPattern.test(value)) {
        return extractStringFromHTML(message)
      }

      return ''
    }
  }, [businessEntity])

  // Sync local value with form value when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalValue(values.gig_identifier)
      setLocalError(errors.gig_identifier || '') // Clear any previous errors
    }
  }, [errors.gig_identifier, isOpen, values.gig_identifier])

  // Validate local value whenever it changes
  useEffect(() => {
    const error = validateGigIdentifier(localValue)
    setLocalError(error)
  }, [localValue, validateGigIdentifier])

  const handleSave = () => {
    if (!localError) {
      setFieldValue('gig_identifier', localValue)
      closeModal()
    }
  }

  const handleCloseModal = () => {
    setLocalValue(values.gig_identifier)
    setLocalError(errors.gig_identifier || '')
    closeModal()
  }

  return (
    <Modal isOpen={isOpen} closeModal={handleCloseModal} width='600px' height='250px'>
      <Header title='Shift Identifier Numbers' />

      <Content>
        <InputV2
          label='Shift Identifier Numbers'
          name='gig_identifier'
          id='gig_identifier'
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !localError) {
              e.preventDefault()
              handleSave()
            }
          }}
          errorMessage={localError}
        />
      </Content>

      <FooterWrapper>
        <Button onClick={handleSave} isDisabled={!!localError}>
          Save
        </Button>
      </FooterWrapper>
    </Modal>
  )
})
